import { FC, useState } from "react"
import { Search, X } from "lucide-react"

type SearchInputProps = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}

export const SearchInput: FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder = "Search...",
  className = ""
}) => {
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div className={`relative ${className}`}>
      <div className={`
        relative flex items-center gap-2 px-4 py-2.5 rounded-full
        bg-white/60 dark:bg-neutral800/60
        border-2 backdrop-blur-md
        transition-all duration-300 ease-out
        ${isFocused
          ? "border-sumoPrimary shadow-lg shadow-sumoPrimary/20 bg-white/80 dark:bg-neutral800/80"
          : "border-neutral200/50 dark:border-neutral700/50 hover:border-neutral300 dark:hover:border-neutral600"
        }
      `}>
        {/* Search Icon */}
        <Search
          className={`flex-shrink-0 transition-colors duration-200 ${
            isFocused
              ? "text-sumoPrimary"
              : "text-neutral400 dark:text-neutral500"
          }`}
          width={18}
          height={18}
        />

        {/* Input */}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className="
            flex-1 bg-transparent border-none outline-none
            text-neutral900 dark:text-neutral100
            placeholder:text-neutral400 dark:placeholder:text-neutral500
            text-sm font-medium
          "
        />

        {/* Clear Button */}
        {value && (
          <button
            onClick={() => onChange("")}
            className="
              flex-shrink-0 p-1 rounded-full
              bg-neutral200/50 dark:bg-neutral700/50
              hover:bg-neutral300/80 dark:hover:bg-neutral600/80
              transition-all duration-200
              hover:scale-110 active:scale-95
            "
            aria-label="Clear search"
          >
            <X
              className="text-neutral600 dark:text-neutral300"
              width={14}
              height={14}
            />
          </button>
        )}
      </div>

      {/* Glow Effect when Focused */}
      {isFocused && (
        <div className="absolute inset-0 rounded-full bg-sumoPrimary/10 blur-xl -z-10 animate-pulse" />
      )}
    </div>
  )
}
